import { Component } from '@angular/core';
import { Save, X, CheckCircle, Check, XCircle, Archive, ArchiveRestore } from 'lucide-angular';
import { LucideAngularModule } from 'lucide-angular';
import { UserModel } from '../shared/user.model';
import { UserService } from '../shared/user.service';
import { ROLE_CHOICES, roleLabels } from '../shared/user-roles';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AbstractEntityEditComponent } from '@app/shared/abstract/abstract-entity-edit.component';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { VenueService } from '@app/venues/shared/venue.service';
import { RouteParams } from '@app/shared/abstract/route-params';
import { map, Observable } from 'rxjs';
import { PasswordValidators } from '@app/shared/validators/password.validator';
import { NotificationService } from '@app/shared/notification/notification.service';
import { AuthService } from '@app/auth/auth.service';
import { AlertComponent } from '@app/partials/ui/alert/alert.component';

interface ArchivedUserConflict {
  archivedUser: UserModel;
  matchedField: 'email' | 'second_email';
  message: string;
  submittedEmail: string;
  submittedSecondEmail: string;
}

@Component({
  selector: 'app-edit',
  imports: [ButtonComponent, ReactiveFormsModule, SelectComponent, TextInputComponent, LucideAngularModule, AlertComponent],
  templateUrl: './user-edit.component.html',
  styleUrl: './user-edit.component.css',
})
export class UserEditComponent extends AbstractEntityEditComponent {
  protected processing = false;
  protected validationErrors: string[] = [];
  protected user: UserModel;
  protected readonly SaveIcon = Save;
  protected readonly XIcon = X;
  protected readonly CheckCircleIcon = CheckCircle;
  protected readonly CheckIcon = Check;
  protected readonly XCircleIcon = XCircle;
  protected readonly ArchiveIcon = Archive;
  protected readonly RestoreIcon = ArchiveRestore;
  protected currentUser: UserModel | null = null;
  protected isAdmin = false;
  protected archivedConflict: ArchivedUserConflict | null = null;

  roleChoices = ROLE_CHOICES;

  form = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]),
    email: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    second_email: new FormControl('', [Validators.maxLength(255)]),
    password: new FormControl('', [PasswordValidators.strong()]),
    role: new FormControl<string>('', [Validators.required]),
    venues: new FormControl<number[]>([], Validators.required),
  });

  passwordErrors: string[] = [];

  // Password requirement checks
  passwordRequirements = {
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false
  };

  protected venueChoices: Observable<{ label: string; value: number | null }[]>;

  constructor(
    private userService: UserService,
    private venueService: VenueService,
    private route: ActivatedRoute,
    private router: Router,
    private notificationService: NotificationService,
    private authService: AuthService,
  ) {
    super();

    // Check if current user is admin
    this.authService.getUser().subscribe({
      next: (user) => {
        this.currentUser = user;
        this.isAdmin = user?.roles?.includes('admin') ?? false;
        this.updateRoleControlState();
      }
    });

    this.form.valueChanges.subscribe((value: any) => {
      if (this.form.dirty && !this.form.valid) {
        this.validationErrors = this.getFormValidationErrors(this.form);
      }

      if (this.hasArchivedConflictEmailChanged()) {
        this.archivedConflict = null;
      }

      // Update password-specific errors
      const passwordControl = this.form.get('password');
      if (passwordControl && passwordControl.errors && passwordControl.touched) {
        this.passwordErrors = PasswordValidators.getErrorMessages(passwordControl.errors);
      } else {
        this.passwordErrors = [];
      }

      // Update password requirements dynamically
      this.updatePasswordRequirements();
    });

    this.venueChoices = this.venueService
      .getPage(new RouteParams({ pageSize: 1000, pageNum: 1 }))
      .pipe(map((res) => res.data.map((item) => ({ value: item.id, label: item.name }))));

    if (this.route.snapshot.data['user']) {
      this.user = this.route.snapshot.data['user'];
      this.form.patchValue(this.user as any);
      if (this.user.roles) {
        this.form.get('role')?.setValue(this.user.roles[0]);
      }
      // For existing users, password is optional
      this.form.get('password')?.clearValidators();
      this.form.get('password')?.setValidators([PasswordValidators.strong()]);
      this.form.get('password')?.updateValueAndValidity();
      this.form.controls['venues'].setValue(this.user.venues.map((venue) => venue.id as number));
      this.updateRoleControlState();
    } else {
      this.user = {} as UserModel;
      this.form.patchValue(this.user as any);
      // For new users, password is required
      this.form.get('password')?.setValidators([Validators.required, PasswordValidators.strong()]);
      this.form.get('password')?.updateValueAndValidity();
      this.updateRoleControlState();
    }
  }

  save() {
    this.performSave(false);
  }

  saveAndContinue() {
    this.performSave(true);
  }

  private performSave(continueEditing: boolean) {
    if (this.form.invalid) {
      return;
    }
    this.processing = true;
    this.archivedConflict = null;

    const data = this.buildPayload();

    this.userService.save(data as unknown as UserModel).subscribe({
      next: (res) => {
        this.processing = false;
        this.applySavedUser(res.data);
        this.notificationService.success('User saved successfully');

        if (continueEditing) {
          // For saveAndContinue, stay on edit page
          if (!this.user.id && res.data) {
            this.router.navigate(['/users/' + res.data.id + '/edit']);
          }
          // If editing existing user, just stay on current page (no navigation)
        } else {
          // For regular save, go to index page
          this.router.navigate(['/users']);
        }
      },
      error: (res) => {
        if (res.status === 422) {
          // Map backend 'roles' errors to frontend 'role' field
          if (res.error.errors && res.error.errors.roles) {
            res.error.errors.role = res.error.errors.roles;
            delete res.error.errors.roles;
          }
          AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
          this.notificationService.error('Please correct the validation errors');
        } else if (res.status === 409 && res.error?.archived_user) {
          this.archivedConflict = {
            archivedUser: res.error.archived_user,
            matchedField: res.error.matched_field ?? 'email',
            message: res.error.message ?? 'An archived user matches this email.',
            submittedEmail: this.form.get('email')?.value ?? '',
            submittedSecondEmail: this.form.get('second_email')?.value ?? '',
          };
          this.notificationService.warning('An archived user was found for this email. Review the reactivation options below.');
        } else {
          this.notificationService.error('An error occurred while saving the user');
        }
        this.processing = false;
      },
    });
  }

  cancel() {
    return this.router.navigate(['/users']);
  }

  protected getForms(): FormGroup<any>[] {
    return [this.form];
  }

  updatePasswordRequirements(): void {
    const password = this.form.get('password')?.value || '';

    this.passwordRequirements = {
      minLength: password.length >= 8,
      hasUpperCase: /\p{Lu}/u.test(password),
      hasLowerCase: /\p{Ll}/u.test(password),
      hasNumber: /\p{N}/u.test(password),
      hasSpecialChar: /[^\p{L}\p{N}\s]/u.test(password)
    };
  }

  get showPasswordRequirements(): boolean {
    const passwordControl = this.form.get('password');
    return !this.user.id || !!(passwordControl?.value && passwordControl.value.length > 0);
  }

  async archiveUser() {
    if (!this.user.id) return;

    const confirmed = await this.confirmationModal.confirm({
      title: 'Archive user',
      message: `Are you sure you want to archive ${this.user.name}?`,
      confirmLabel: 'Archive User',
      cancelLabel: 'Keep User',
      confirmButtonStyle: 'danger',
    });

    if (!confirmed) {
      return;
    }

    this.userService.archive(this.user.id).subscribe({
      next: (response) => {
        this.notificationService.success(response.message);
        this.router.navigate(['/users']);
      },
      error: (error) => {
        this.notificationService.error(error.error?.message || 'Failed to archive user');
      }
    });
  }

  async restoreUser() {
    if (!this.user.id) return;

    const confirmed = await this.confirmationModal.confirm({
      title: 'Restore user',
      message: `Are you sure you want to restore ${this.user.name}?`,
      confirmLabel: 'Restore User',
      cancelLabel: 'Cancel',
      confirmButtonStyle: 'success',
    });

    if (!confirmed) {
      return;
    }

    this.userService.restore(this.user.id).subscribe({
      next: () => {
        this.notificationService.success('User restored successfully');
        // Reload the page to show updated status
        window.location.reload();
      },
      error: (error) => {
        this.notificationService.error(error.error?.message || 'Failed to restore user');
      }
    });
  }

  get canArchive(): boolean {
    return this.isAdmin && !!this.user.id && !this.user.deleted_at && !this.user.roles?.includes('admin');
  }

  get canRestore(): boolean {
    return this.isAdmin && !!this.user.id && !!this.user.deleted_at;
  }

  async reactivateArchivedUser() {
    if (!this.archivedConflict?.archivedUser?.id) {
      return;
    }

    const userName = this.archivedConflict.archivedUser.name || 'this user';

    const confirmed = await this.confirmationModal.confirm({
      title: 'Reactivate archived user',
      message: `Reactivate ${userName} using the current form details?`,
      confirmLabel: 'Reactivate User',
      cancelLabel: 'Cancel',
      confirmButtonStyle: 'success',
    });

    if (!confirmed) {
      return;
    }

    this.processing = true;

    this.userService
      .reactivateArchived({
        ...this.buildPayload(),
        archived_user_id: this.archivedConflict.archivedUser.id,
      })
      .subscribe({
        next: (res) => {
          this.processing = false;
          this.archivedConflict = null;
          this.applySavedUser(res.data);
          this.notificationService.success('User reactivated successfully');
          this.router.navigate(['/users', res.data.id, 'edit']);
        },
        error: (res) => {
          if (res.status === 422) {
            if (res.error.errors && res.error.errors.roles) {
              res.error.errors.role = res.error.errors.roles;
              delete res.error.errors.roles;
            }
            AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
            this.notificationService.error('Please correct the validation errors');
          } else {
            this.notificationService.error(res.error?.message || 'Failed to reactivate user');
          }

          this.processing = false;
        },
      });
  }

  dismissArchivedConflict() {
    this.archivedConflict = null;
  }

  get archivedConflictRoles(): string {
    const roles = this.archivedConflict?.archivedUser?.roles ?? [];

    return roles.length > 0 ? roleLabels(roles) : 'No role assigned';
  }

  private updateRoleControlState(): void {
    const roleControl = this.form.controls.role;

    if (this.shouldDisableRoleControl()) {
      if (roleControl.enabled) {
        roleControl.disable({ emitEvent: false });
      }

      return;
    }

    if (roleControl.disabled) {
      roleControl.enable({ emitEvent: false });
    }
  }

  private shouldDisableRoleControl(): boolean {
    // Admin user roles are immutable in current backend rules.
    return !!this.user?.id && (this.user.roles ?? []).includes('admin');
  }

  private buildPayload() {
    const venues = this.form.get('venues')?.value || [];

    return {
      id: this.user.id || null,
      created_at: this.user.created_at || null,
      updated_at: this.user.updated_at || null,
      name: this.form.get('name')?.value,
      email: this.form.get('email')?.value,
      second_email: this.form.get('second_email')?.value,
      password: this.form.get('password')?.value || null,
      roles: [this.form.get('role')?.value],
      venues,
    };
  }

  private applySavedUser(user: UserModel) {
    this.user = user;
    this.form.patchValue(this.user as any);
    this.form.controls['venues'].setValue((this.user.venues ?? []).map((venue) => venue.id as number));
    this.form.markAsPristine();
  }

  private hasArchivedConflictEmailChanged(): boolean {
    if (!this.archivedConflict) {
      return false;
    }

    return (
      (this.form.get('email')?.value ?? '') !== this.archivedConflict.submittedEmail ||
      (this.form.get('second_email')?.value ?? '') !== this.archivedConflict.submittedSecondEmail
    );
  }
}
